import {
  Avatar,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  NextUIProvider,
  Select,
  SelectItem,
  useDisclosure,
} from "@nextui-org/react";
import { Header } from "./components/Header";
import CookieOutlineIcon from "./svgIcons/CookieOutlineIcon";
import DesktopOutlineIcon from "./svgIcons/DesktopOutlineIcon";
import { SunIcon } from "./svgIcons/SunIcon";
import { MoonIcon } from "./svgIcons/MoonIcon";
import React, { useContext, useEffect } from "react";
import { ThemeContext } from "./lib/contexts/ThemeContext";
import { ThemeContextInterface } from "./lib/types/ThemeType";
import MaintenancePage from "./components/MaintenancePage";
import MobilePage from "./components/MobilePage";

function App() {
  const isMaintenance = process.env.REACT_APP_IN_MAINTENANCE_MODE;
  const seMaintenanceTitle =
    process.env.REACT_APP_IN_MAINTENANCE_SE_TITLE || "";
  const seMaintenanceBody = process.env.REACT_APP_IN_MAINTENANCE_SE_BODY || "";
  const enMaintenanceTitle =
    process.env.REACT_APP_IN_MAINTENANCE_EN_TITLE || "";
  const enMaintenanceBody = process.env.REACT_APP_IN_MAINTENANCE_EN_BODY || "";

  const seAboutPageHeader = process.env.REACT_APP_ABOUT_SE_HEADER || "";
  const seAboutPageBody = process.env.REACT_APP_ABOUT_SE_BODY || "";
  const enAboutPageHeader = process.env.REACT_APP_ABOUT_EN_HEADER || "";
  const enAboutPageBody = process.env.REACT_APP_ABOUT_EN_BODY || "";

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { darkTheme, toggleTheme } = useContext(
    ThemeContext
  ) as ThemeContextInterface;
  const [ThemeColorState, SetThemeColorState] =
    React.useState<string>("system");
  const [LanguageState, SetLanguageState] = React.useState<string>("swedish");
  const [hasLanguage, setHasLanguage] = React.useState(false);
  const [LogoState, SetLogoState] = React.useState<string>(
    "/lunet-logo-gray.png"
  );

  useEffect(() => {
    if (
      !("theme" in localStorage) &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      document.documentElement.classList.add("dark");
      SetLogoState("/lunet-logo-white.png");
      SetThemeColorState("system");
    } else if (
      !("theme" in localStorage) &&
      window.matchMedia("(prefers-color-scheme: light)").matches
    ) {
      document.documentElement.classList.remove("dark");
      SetLogoState("/lunet-logo-gray.png");
      SetThemeColorState("system");
    } else if (localStorage.theme === "dark") {
      document.documentElement.classList.add("dark");
      SetLogoState("/lunet-logo-white.png");
      SetThemeColorState("dark");
    } else {
      document.documentElement.classList.remove("dark");
      SetLogoState("/lunet-logo-gray.png");
      SetThemeColorState("light");
    }

    if (!("lang" in localStorage)) {
      SetLanguageState("swedish");
    } else if (localStorage.lang === "swedish") {
      SetLanguageState("swedish");
      setHasLanguage(true);
    } else {
      SetLanguageState("english");
      setHasLanguage(true);
    }
  }, [darkTheme]);

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    toggleTheme();
    //console.log(e.target.value);
    SetThemeColorState(e.target.value);
    if (e.target.value === "system") {
      if (
        localStorage.theme === "dark" ||
        (!("theme" in localStorage) &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        SetLogoState("/lunet-logo-white.png");
      } else {
        SetLogoState("/lunet-logo-gray.png");
      }
      localStorage.removeItem("theme");
    } else if (e.target.value === "dark") {
      localStorage.theme = "dark";
      SetLogoState("/lunet-logo-white.png");
    } else {
      localStorage.theme = "light";
      SetLogoState("/lunet-logo-gray.png");
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    SetLanguageState(e.target.value);
    if (e.target.value === "swedish") {
      localStorage.lang = "swedish";
      SetLanguageState("swedish");
      //console.log("Language = Swedish");
    } else {
      localStorage.lang = "english";
      SetLanguageState("english");
      //console.log("Language = English");
    }
  };

  const handleInitialLanguage = (lang: string) => {
    setHasLanguage(true);
    SetLanguageState(lang);
    localStorage.lang = lang;
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.innerHTML = `window.addEventListener("load", function(){progressier.add({tags: "${ThemeColorState} , ${LanguageState}", theme: "${ThemeColorState}", language: "${LanguageState}"});});`;
    document.body.appendChild(script);
  }, [ThemeColorState, LanguageState]);

  return (
    <NextUIProvider>
      <div className="wrapper">
        {hasLanguage ? (
          <div className="scroll-wrapper">
            <Header onOpen={onOpen} logo={LogoState} />
            {isMaintenance === "true" ? (
              <div className="wrapper-content overflow-auto flex w-full h-full justify-center text-zinc-900 dark:text-zinc-100 bg-zinc-100 dark:bg-zinc-900">
                <MaintenancePage
                  LanguageState={LanguageState}
                  seMaintenanceTitle={seMaintenanceTitle}
                  seMaintenanceBody={seMaintenanceBody}
                  enMaintenanceTitle={enMaintenanceTitle}
                  enMaintenanceBody={enMaintenanceBody}
                />
              </div>
            ) : (
              <div className="wrapper-content overflow-auto flex w-full h-full px-safe justify-center text-zinc-900 dark:text-zinc-100 bg-zinc-100 dark:bg-zinc-900">
                <MobilePage
                  LanguageState={LanguageState}
                  seAboutPageHeader={seAboutPageHeader}
                  seAboutPageBody={seAboutPageBody}
                  enAboutPageHeader={enAboutPageHeader}
                  enAboutPageBody={enAboutPageBody}
                />
              </div>
            )}
            <Modal
              size="md"
              backdrop="opaque"
              placement="center"
              scrollBehavior="inside"
              isOpen={isOpen}
              onOpenChange={onOpenChange}
            >
              <ModalContent>
                <ModalHeader className="flex flex-col gap-1 text-sm">
                  {LanguageState === "swedish" ? (
                    <p>Inställningar</p>
                  ) : (
                    <p>Settings</p>
                  )}
                </ModalHeader>
                <ModalBody>
                  <Select
                    size="sm"
                    className="w-full font-buttons"
                    label={
                      LanguageState === "swedish" ? (
                        <p>Utseende</p>
                      ) : (
                        <p>Appearance</p>
                      )
                    }
                    selectionMode="single"
                    selectedKeys={[ThemeColorState]}
                    onChange={handleThemeChange}
                  >
                    <SelectItem
                      data-umami-event="Theme - Automatic"
                      key="system"
                      value="system"
                      className="font-buttons"
                      startContent={<DesktopOutlineIcon />}
                    >
                      {LanguageState === "swedish"
                        ? "Automatiskt"
                        : "Automatic"}
                    </SelectItem>
                    <SelectItem
                      data-umami-event="Theme - Light"
                      key="light"
                      value="light"
                      className="font-buttons"
                      startContent={<SunIcon />}
                    >
                      {LanguageState === "swedish" ? "Ljust" : "Light"}
                    </SelectItem>
                    <SelectItem
                      data-umami-event="Theme - Dark"
                      key="dark"
                      value="dark"
                      className="font-buttons"
                      startContent={<MoonIcon />}
                    >
                      {LanguageState === "swedish" ? "Mörkt" : "Dark"}
                    </SelectItem>
                  </Select>
                  <Select
                    size="sm"
                    className="w-full font-buttons"
                    label={
                      LanguageState === "swedish" ? (
                        <p>Språk</p>
                      ) : (
                        <p>Language</p>
                      )
                    }
                    selectionMode="single"
                    selectedKeys={[LanguageState]}
                    onChange={handleLanguageChange}
                  >
                    <SelectItem
                      data-umami-event="Language - Swedish"
                      key="swedish"
                      value="swedish"
                      className="font-buttons"
                      startContent={
                        <Avatar
                          alt="Svenska"
                          className="w-6 h-6"
                          src="https://flagcdn.com/se.svg"
                        />
                      }
                    >
                      Svenska
                    </SelectItem>
                    <SelectItem
                      data-umami-event="Language - English"
                      key="english"
                      value="english"
                      className="font-buttons"
                      startContent={
                        <Avatar
                          alt="English"
                          className="w-6 h-6"
                          src="https://flagcdn.com/gb.svg"
                        />
                      }
                    >
                      English
                    </SelectItem>
                  </Select>
                </ModalBody>
                <ModalFooter className="flex flex-col font-buttons">
                  <Button
                    data-umami-event="Manage Cookie Settings"
                    size="sm"
                    color="warning"
                    variant="flat"
                    className="cky-banner-element m-auto"
                    startContent={<CookieOutlineIcon />}
                  >
                    {LanguageState === "swedish" ? (
                      <p>Hantera cookies-inställningar</p>
                    ) : (
                      <p>Manage cookie settings</p>
                    )}
                  </Button>
                  <div className="text-zinc-900/70 dark:text-white/50 text-xs text-center">
                    {LanguageState === "swedish"
                      ? "Utgåva 1.2.4"
                      : "Version 1.2.4"}
                  </div>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </div>
        ) : (
          <Modal
            isDismissable={false}
            hideCloseButton
            size="xs"
            placement="center"
            scrollBehavior="inside"
            isOpen={!hasLanguage}
            onClose={() => !hasLanguage}
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-1 text-sm">
                <p>Välj Språk / Choose Language</p>
              </ModalHeader>
              <ModalBody>
                <div className="flex w-full gap-8 items-center justify-center mb-4">
                  <Button
                    data-umami-event="Initial Language - Swedish"
                    color="default"
                    variant="light"
                    className="flex flex-col h-auto p-2"
                    onPress={() => handleInitialLanguage("swedish")}
                  >
                    <Avatar src="https://flagcdn.com/se.svg" size="md" />
                    Svenska
                  </Button>
                  <Button
                    data-umami-event="Initial Language - English"
                    color="default"
                    variant="light"
                    className="flex flex-col h-auto p-2"
                    onPress={() => handleInitialLanguage("english")}
                  >
                    <Avatar src="https://flagcdn.com/gb.svg" size="md" />
                    English
                  </Button>
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </div>
    </NextUIProvider>
  );
}

export default App;
